class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.onSwatchChange = this.onSwatchChange.bind(this);
  }

  connectedCallback() {
    this.wrapper = this.closest('product-form-component');
    if (this.closest('product-info')) {
      this.wrapper = this.closest('product-info');
    }

    this.init();

    window.addEventListener('product:swatch-change', this.onSwatchChange);
  }

  disconnectedCallback() {
    window.removeEventListener('product:swatch-change', this.onSwatchChange);
    if (this.selectors) {
      this.selectors.forEach((selector) => {
        selector.removeEventListener('change', this.handleVariantChange);
      });
    }
  }

  onSwatchChange(event) {
    if (event.detail.productCardForm == null || event.detail.productCardForm !== this.wrapper) {
      return;
    }

    this.handleVariantChange(null, false, +event.detail.variantId);
  }

  init() {
    this.options = {
      isQuickShop: false,
      isPreorder: false,
    };
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    let currentVariantId = parseInt(this.wrapper.querySelector('input[name="id"]')?.value);
    if (!currentVariantId || isNaN(currentVariantId)) {
      currentVariantId = this.getVariantBySelectedInputs()?.id;
    }
    this.variantId = currentVariantId;
    this.selectedVariant = this.getCurrentVariant(currentVariantId);

    if (this.selectors) {
      this.selectors.forEach((selector) => {
        selector.removeEventListener('change', this.handleVariantChange);
      });
    }

    this.selectors = this.querySelectorAll('input[type="radio"]');
    this.selectors.forEach((selector) => {
      selector.addEventListener('change', this.handleVariantChange);
    });
  }

  getCurrentVariant(id) {
    return this.getVariantData().find(variant => variant.id === +id);
  }

  handleVariantChange(event, initFirst = false, id = null) {
    if (initFirst) {
      this.init();
      this.variantId = this.selectedVariant.id;
    }
    else if (id) {
      this.variantId = id;
      this._prefetchedHTML = null;
    }
    else if (event) {
      this._prefetchedHTML = null;
      const changedFieldset = event.target.closest('fieldset[data-option-group]');
      const changedGroupIndex = changedFieldset ? parseInt(changedFieldset.dataset.optionGroup) : -1;
      const variant = this.getVariantBySelectedInputs(changedGroupIndex);
      if (variant) {
        this.variantId = variant.id;
      }
    }

    this.newVariant = this.getCurrentVariant(this.variantId);

    if (this.options.isQuickShop) {
      return this.fetchProductInfo();
    }
    else {
      this.renderProductInfoFromSection();
    }
  }

  fetchProductInfo() {
    const url = `${this.options.url}?variant=${this.variantId}&section_id=theme-quick-shop`;
    const textPromise = this._prefetchedHTML ?? fetch(url).then(response => response.text());

    return textPromise
      .then((data) => {
        const html = document.createRange().createContextualFragment(data);
        const productForm = this.wrapper;

        productForm.querySelector('[data-quick-shop-options-wrapper]').innerHTML = html.querySelector('#shopify-section-theme-quick-shop').innerHTML;
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error fetching product info:', error);
        }
      });
  }

  renderProductInfoFromSection() {
    const url = `${this.options.url}?variant=${this.variantId}&section_id=${this.options.sectionId}`;
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = document.createRange().createContextualFragment(responseText);

        const productInfo = document.querySelector('[data-product-info]');
        if (html.querySelector('[data-product-info]')) {
          productInfo.innerHTML = html.querySelector('[data-product-info]').innerHTML;
        }

        // Re-read the actual resolved variant from the freshly rendered DOM
        const renderedVariantInput = productInfo.querySelector('input[name="id"]');
        if (renderedVariantInput) {
          this.newVariant = this.getCurrentVariant(parseInt(renderedVariantInput.value));
        }

        this.updateURL(url);
        if (this.newVariant) {
          this.dispatchVariantChange(this.newVariant.id);
        }

        // On the PDP, also update the quick add input
        if (this.newVariant && document.querySelector('sticky-add-to-cart')) {
          document.querySelector('sticky-add-to-cart [data-quick-shop-options-wrapper] input[name="id"]').value = this.newVariant.id;
        }
      });
  }

  dispatchVariantChange(variantId) {
    document.dispatchEvent(
      new CustomEvent('product:variant-change', {
        bubbles: true,
        detail: {
          variantId,
        },
      }),
    );
  }

  updateURL(url) {
    const newUrl = new URL(url, window.location.origin);
    newUrl.searchParams.delete('section_id');
    window.history.pushState({}, '', newUrl.toString());
  }

  getVariantBySelectedInputs(changedGroupIndex = -1) {
    const checkedInputs = Array.from(this.wrapper.querySelectorAll('input[type="radio"]:checked'));
    const selectedOptions = {};
    checkedInputs.forEach((input) => {
      const fieldset = input.closest('fieldset[data-option-group]');
      if (fieldset) {
        const groupIndex = parseInt(fieldset.dataset.optionGroup);
        selectedOptions[groupIndex] = input.value;
      }
    });

    // Try exact match across all selected options
    const exactMatch = this.getVariantData().find(variant =>
      Object.entries(selectedOptions).every(([index, value]) =>
        variant[`option${parseInt(index) + 1}`] === value,
      ),
    );
    if (exactMatch) return exactMatch;

    // No exact match (e.g. switching option1 type invalidates the current option2 value).
    // Fall back to the first available variant that matches the changed option only.
    if (changedGroupIndex >= 0) {
      const changedValue = selectedOptions[changedGroupIndex];
      const optionKey = `option${changedGroupIndex + 1}`;
      return (
        this.getVariantData().find(v => v[optionKey] === changedValue && v.available) ||
        this.getVariantData().find(v => v[optionKey] === changedValue)
      );
    }

    return this.getVariantData()[0];
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}
